import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { insightsAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useToast } from '../hooks/use-toast';
import {
  TrendingUp, TrendingDown, DollarSign, ArrowUpRight, ArrowDownRight,
  BarChart3, PieChart as PieChartIcon, AlertTriangle, Target, Wallet,
  Calendar, Building, RefreshCw, ArrowRight, Info, CheckCircle,
  XCircle, Clock, Minus
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, ComposedChart,
  Area, PieChart, Pie, Cell, Legend, ReferenceLine
} from 'recharts';

const SEVERITY_STYLES = {
  critical: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', icon: XCircle, badge: 'destructive' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', icon: AlertTriangle, badge: 'default' },
  info: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', icon: Info, badge: 'secondary' },
  success: { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', icon: CheckCircle, badge: 'default' }
};

const PIE_COLORS = ['#1F3A68', '#CB3B3B', '#10B981', '#F59E0B', '#6366F1', '#EC4899'];

export default function FinancialInsights({ onNavigate }) {
  const { token } = useAuth();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState('alerts');
  const [smartAlerts, setSmartAlerts] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [propertyPnl, setPropertyPnl] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState({});
  const [pnlPeriod, setPnlPeriod] = useState('12months');
  const [expensePeriod, setExpensePeriod] = useState('12months');
  const [aiPeriod, setAiPeriod] = useState('12months');
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const [savingTarget, setSavingTarget] = useState(false);
  const [pnlDetail, setPnlDetail] = useState(null);
  const [cashflowGranularity, setCashflowGranularity] = useState('weekly');
  const [cashflowStart, setCashflowStart] = useState('');

  const loadData = useCallback(async (key, fetcher) => {
    if (!token) return;
    setLoading(prev => ({ ...prev, [key]: true }));
    try {
      return await fetcher();
    } catch (err) {
      console.error(`Failed to load ${key}:`, err);
      return null;
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadData('alerts', () => insightsAPI.getSmartAlerts(token)).then(d => d && setSmartAlerts(d));
  }, [token, loadData]);

  useEffect(() => {
    if (!token) return;
    if (activeTab === 'forecast') {
      loadData('forecast', () => insightsAPI.getRevenueForecast(token, 3)).then(d => d && setForecast(d));
    }
    if (activeTab === 'cashflow') {
      const periods = cashflowGranularity === 'monthly' ? 6 : 8;
      loadData('cashflow', () => insightsAPI.getCashflow(token, { granularity: cashflowGranularity, periods, startDate: cashflowStart || null }))
        .then(d => d && setCashflow(d));
    }
    if (activeTab === 'pnl') {
      loadData('pnl', () => insightsAPI.getPropertyPnl(token, pnlPeriod)).then(d => d && setPropertyPnl(d));
    }
    if (activeTab === 'expenses') {
      loadData('expenses', () => insightsAPI.getExpenseSummary(token, expensePeriod)).then(d => d && setExpenses(d));
    }
    if (activeTab === 'benchmarks') {
      loadData('benchmarks', () => insightsAPI.getBenchmarks(token)).then(d => d && setBenchmarks(d));
    }
    if (activeTab === 'ai-insights') {
      loadData('ai-insights', () => insightsAPI.getAiPnlInsights(token, aiPeriod)).then(d => d && setAiInsights(d));
    }
  }, [activeTab, token, pnlPeriod, expensePeriod, aiPeriod, cashflowGranularity, cashflowStart, loadData]);

  const Loader = () => (
    <div className="flex items-center justify-center py-16">
      <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
    </div>
  );

  const saveTarget = async () => {
    const value = parseFloat(targetInput);
    if (isNaN(value) || value < 0) {
      toast({ title: 'Enter a valid amount', variant: 'destructive' });
      return;
    }
    setSavingTarget(true);
    try {
      await insightsAPI.setEarningsTarget(token, value);
      toast({ title: 'Target saved', description: `Monthly earnings target set to £${value.toLocaleString()}` });
      setEditingTarget(false);
      const fresh = await insightsAPI.getBenchmarks(token);
      if (fresh) setBenchmarks(fresh);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Could not save target', variant: 'destructive' });
    } finally {
      setSavingTarget(false);
    }
  };

  const TrendBadge = ({ direction, change }) => {
    if (direction === 'up') return (
      <span className="inline-flex items-center text-xs text-emerald-600 font-medium">
        <ArrowUpRight className="h-3 w-3" /> {Math.abs(change)}%
      </span>
    );
    if (direction === 'down') return (
      <span className="inline-flex items-center text-xs text-red-600 font-medium">
        <ArrowDownRight className="h-3 w-3" /> {Math.abs(change)}%
      </span>
    );
    return <span className="text-xs text-gray-400">—</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-[#1F3A68]">Financial Insights</h2>
        <p className="text-sm text-gray-500 mt-1">Revenue forecasts, cash flow projections, and smart business alerts</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="alerts" className="relative">
            Alerts
            {smartAlerts?.summary?.total > 0 && (
              <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center p-0 text-xs">
                {smartAlerts.summary.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="forecast">Forecast</TabsTrigger>
          <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
          <TabsTrigger value="pnl">Property P&L</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="benchmarks">Benchmarks</TabsTrigger>
          <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
        </TabsList>

        {/* ── Smart Alerts ─────────────────────────────────────────────── */}
        <TabsContent value="alerts">
          {smartAlerts ? (
            <div className="space-y-4">
              {/* Summary badges */}
              <div className="flex gap-3">
                {smartAlerts.summary.critical > 0 && (
                  <Badge variant="destructive">{smartAlerts.summary.critical} Critical</Badge>
                )}
                {smartAlerts.summary.warning > 0 && (
                  <Badge className="bg-amber-100 text-amber-700">{smartAlerts.summary.warning} Warnings</Badge>
                )}
                {smartAlerts.summary.info > 0 && (
                  <Badge className="bg-blue-100 text-blue-700">{smartAlerts.summary.info} Info</Badge>
                )}
                {smartAlerts.summary.success > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700">{smartAlerts.summary.success} Positive</Badge>
                )}
              </div>

              {smartAlerts.alerts.length > 0 ? smartAlerts.alerts.map((alert, idx) => {
                const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.info;
                const Icon = style.icon;
                return (
                  <Card key={idx} className={`${style.bg} ${style.border}`}>
                    <CardContent className="py-4 px-5">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${style.text}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className={`font-semibold ${style.text}`}>{alert.title}</h4>
                              <Badge variant="outline" className="text-xs capitalize">{alert.type}</Badge>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                          </div>
                        </div>
                        {alert.action && (
                          <Button variant="outline" size="sm" className="w-full sm:w-auto sm:shrink-0" onClick={() => {
                            if (onNavigate) {
                              // Extract propertyId from actionLink if available (e.g. "/properties/abc123/edit")
                              const linkMatch = alert.actionLink?.match(/\/properties\/([^/]+)/);
                              const propertyId = linkMatch ? linkMatch[1] : null;
                              onNavigate(alert.action, { propertyId, alertType: alert.type });
                            }
                          }}>
                            {alert.action} <ArrowRight className="h-3 w-3 ml-1" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              }) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <CheckCircle className="h-12 w-12 mx-auto text-emerald-400 mb-3" />
                    <h3 className="text-lg font-semibold text-emerald-600">All Clear</h3>
                    <p className="text-sm text-gray-400 mt-1">No alerts at the moment. Your business is running smoothly.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : <Loader />}
        </TabsContent>

        {/* ── Revenue Forecast ─────────────────────────────────────────── */}
        <TabsContent value="forecast">
          {forecast ? (
            <div className="space-y-6">
              <p className="flex items-start gap-2 text-xs text-gray-400">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Forecasts are estimates based on your confirmed and pending bookings plus your historical booking patterns. Anything beyond confirmed revenue is a projection, and actual results may vary.
              </p>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Total Forecast</div>
                    <div className="text-2xl font-bold text-[#1F3A68]">
                      £{forecast.summary.totalForecastRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-gray-400">Next {forecast.forecastMonths} months</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Confirmed Revenue</div>
                    <div className="text-2xl font-bold text-emerald-600">
                      £{forecast.summary.confirmedRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-emerald-500">From confirmed bookings</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Historical Avg</div>
                    <div className="text-2xl font-bold text-gray-600">
                      £{forecast.summary.avgMonthlyHistorical.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-gray-400">Per month</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Conversion Rate</div>
                    <div className="text-2xl font-bold text-[#1F3A68]">
                      {(forecast.summary.pendingConversionRate * 100).toFixed(0)}%
                    </div>
                    <div className="text-xs text-gray-400">Pending bookings</div>
                  </CardContent>
                </Card>
              </div>

              {/* Forecast chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Revenue Forecast by Month</CardTitle>
                  <CardDescription>Confirmed bookings vs total forecast with breakdown line</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={forecast.months}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} />
                      <RechartsTooltip
                        formatter={(v, name) => [`£${v.toFixed(2)}`, name]}
                        labelFormatter={(label) => label}
                      />
                      <Legend />
                      <Bar dataKey="confirmedRevenue" name="Confirmed" fill="#10B981" radius={[4, 4, 0, 0]} barSize={40} />
                      <Bar dataKey="pendingRevenue" name="Pending" fill="#F59E0B" radius={[4, 4, 0, 0]} barSize={40} />
                      <Line dataKey="totalForecast" name="Total Forecast" stroke="#1F3A68" strokeWidth={2} dot={{ r: 5, fill: '#1F3A68' }} strokeDasharray="5 5" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Monthly breakdown table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Monthly Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium text-gray-500">Month</th>
                          <th className="text-right py-2 font-medium text-emerald-600">Confirmed</th>
                          <th className="text-right py-2 font-medium text-amber-600">Pending</th>
                          <th className="text-right py-2 font-medium text-gray-400">Projected</th>
                          <th className="text-right py-2 font-medium text-[#1F3A68]">Total Forecast</th>
                          <th className="text-center py-2 font-medium text-gray-500">Confidence</th>
                          <th className="text-center py-2 font-medium text-gray-500">Season</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecast.months.map(m => (
                          <tr key={m.month} className="border-b last:border-0">
                            <td className="py-2 font-medium">{m.label}</td>
                            <td className="py-2 text-right text-emerald-600">
                              £{m.confirmedRevenue.toFixed(2)}
                              {m.confirmedBookings > 0 && <span className="text-xs text-gray-400 ml-1">({m.confirmedBookings})</span>}
                            </td>
                            <td className="py-2 text-right text-amber-600">
                              £{m.pendingRevenue.toFixed(2)}
                              {m.pendingBookings > 0 && <span className="text-xs text-gray-400 ml-1">({m.pendingBookings})</span>}
                            </td>
                            <td className="py-2 text-right text-gray-400">£{m.projectedAdditional.toFixed(2)}</td>
                            <td className="py-2 text-right font-semibold text-[#1F3A68]">£{m.totalForecast.toFixed(2)}</td>
                            <td className="py-2 text-center">
                              <Badge variant="outline" className={
                                m.confidence === 'high' ? 'text-emerald-600 border-emerald-300' :
                                m.confidence === 'medium' ? 'text-amber-600 border-amber-300' :
                                'text-gray-400 border-gray-200'
                              }>
                                {m.confidence}
                              </Badge>
                            </td>
                            <td className="py-2 text-center text-xs text-gray-400">
                              {m.seasonalFactor > 1.1 ? '📈 High' : m.seasonalFactor < 0.9 ? '📉 Low' : '➡️ Normal'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : <Loader />}
        </TabsContent>

        {/* ── Cash Flow ────────────────────────────────────────────────── */}
        <TabsContent value="cashflow">
          {cashflow ? (
            <div className="space-y-6">
              {/* Controls: weekly/monthly + start date */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-lg border border-gray-200 p-0.5">
                  {['weekly', 'monthly'].map((g) => (
                    <button
                      key={g}
                      onClick={() => setCashflowGranularity(g)}
                      className={`px-3 py-1.5 text-sm rounded-md capitalize ${cashflowGranularity === g ? 'bg-[#1F3A68] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Starting</span>
                  <input
                    type="date"
                    value={cashflowStart}
                    onChange={(e) => setCashflowStart(e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  />
                  {cashflowStart && (
                    <button onClick={() => setCashflowStart('')} className="text-gray-400 hover:text-gray-600 text-xs underline">
                      Reset to today
                    </button>
                  )}
                </div>
                {cashflow.isHistorical && (
                  <span className="text-xs text-amber-700 bg-amber-100 rounded-full px-2 py-1">
                    Historical view — running balance is relative to this window, not your bank balance
                  </span>
                )}
              </div>

              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Total Income</div>
                    <div className="text-2xl font-bold text-emerald-600">
                      £{cashflow.summary.totalIncome.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-400">{cashflow.isHistorical ? '' : 'Next '}{cashflow.periods} {cashflow.granularity === 'monthly' ? 'months' : 'weeks'}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Total Expenses</div>
                    <div className="text-2xl font-bold text-red-500">
                      £{cashflow.summary.totalExpenses.toFixed(2)}
                    </div>
                  </CardContent>
                </Card>
                <Card className={cashflow.summary.netCashFlow >= 0 ? '' : 'border-red-300'}>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Net Cash Flow</div>
                    <div className={`text-2xl font-bold ${cashflow.summary.netCashFlow >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      £{cashflow.summary.netCashFlow.toFixed(2)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Monthly Rent</div>
                    <div className="text-2xl font-bold text-gray-600">
                      £{cashflow.summary.monthlyRentCommitment.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-400">Fixed commitment</div>
                  </CardContent>
                </Card>
              </div>

              {/* Cash flow chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{cashflow.granularity === 'monthly' ? 'Monthly' : 'Weekly'} Cash Flow Projection</CardTitle>
                  <CardDescription>Income vs expenses with running balance</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={cashflow.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" tick={{ fontSize: 11 }} tickFormatter={(p) => `${cashflow.granularity === 'monthly' ? 'M' : 'W'}${p}`} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} />
                      <RechartsTooltip
                        formatter={(v, name) => [`£${v.toFixed(2)}`, name]}
                        labelFormatter={(p) => `${cashflow.granularity === 'monthly' ? 'Month' : 'Week'} ${p}`}
                      />
                      <Legend />
                      <Bar dataKey="income" name="Income" fill="#10B981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[4, 4, 0, 0]} />
                      <Line dataKey="runningBalance" name="Running Balance" stroke="#1F3A68" strokeWidth={2} dot={{ r: 4 }} />
                      <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Weekly breakdown */}
              <div className="grid gap-2">
                {cashflow.data.map(week => (
                  <Card key={week.week} className={week.status === 'negative' ? 'border-red-200' : ''}>
                    <CardContent className="py-3 px-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-sm">{week.label}</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <span className="text-emerald-600">
                            <ArrowUpRight className="h-3 w-3 inline" /> £{week.income.toFixed(2)}
                          </span>
                          <span className="text-red-500">
                            <ArrowDownRight className="h-3 w-3 inline" /> £{week.expenses.toFixed(2)}
                          </span>
                          <span className={`font-semibold ${week.netFlow >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {week.netFlow >= 0 ? '+' : ''}£{week.netFlow.toFixed(2)}
                          </span>
                          <span className="text-gray-400 text-xs w-24 text-right">
                            Balance: £{week.runningBalance.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : <Loader />}
        </TabsContent>

        {/* ── Property P&L ─────────────────────────────────────────────── */}
        <TabsContent value="pnl">
          <div className="flex justify-between items-center mb-4">
            <Select value={pnlPeriod} onValueChange={setPnlPeriod}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3months">3 Months</SelectItem>
                <SelectItem value="6months">6 Months</SelectItem>
                <SelectItem value="12months">12 Months</SelectItem>
                <SelectItem value="ytd">Year to Date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {propertyPnl ? (
            <div className="space-y-6">
              {/* Portfolio summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Gross Revenue</div>
                    <div className="text-xl font-bold text-[#1F3A68]">
                      £{propertyPnl.summary.totalGrossRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Net Profit</div>
                    <div className={`text-xl font-bold ${propertyPnl.summary.totalNetProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      £{propertyPnl.summary.totalNetProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Portfolio Margin</div>
                    <div className={`text-xl font-bold ${propertyPnl.summary.portfolioMargin >= 20 ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {propertyPnl.summary.portfolioMargin}%
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-emerald-600 mb-1">Profitable</div>
                    <div className="text-xl font-bold text-emerald-600">{propertyPnl.summary.profitableProperties}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-red-500 mb-1">Unprofitable</div>
                    <div className="text-xl font-bold text-red-600">{propertyPnl.summary.unprofitableProperties}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Property P&L cards */}
              <div className="grid gap-4">
                {propertyPnl.properties.map((prop, idx) => (
                  <Card
                    key={prop.propertyId}
                    className={`cursor-pointer transition-shadow hover:shadow-md hover:border-[#1F3A68]/30 ${prop.netProfit < 0 ? 'border-red-200' : ''}`}
                    onClick={() => setPnlDetail(prop)}
                  >
                    <CardContent className="py-4 px-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white ${prop.netProfit >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}>
                            {idx + 1}
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#1F3A68] flex items-center gap-1">{prop.propertyName} <ArrowRight className="h-3.5 w-3.5 text-gray-300" /></h3>
                            {prop.location && <span className="text-xs text-gray-400">{prop.location}</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-lg font-bold ${prop.netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            £{prop.netProfit.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-400">{prop.profitMargin}% margin</div>
                        </div>
                      </div>

                      {/* P&L breakdown bar */}
                      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 text-xs">
                        <div>
                          <span className="text-gray-400">Gross</span>
                          <div className="font-semibold">£{prop.grossRevenue.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-gray-400">Platform</span>
                          <div className="font-semibold text-red-400">-£{prop.platformFees.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-gray-400">Stripe</span>
                          <div className="font-semibold text-red-400">-£{prop.stripeFees.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-gray-400">Vendors</span>
                          <div className="font-semibold text-red-400">-£{prop.vendorCosts.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-gray-400">Inventory</span>
                          <div className="font-semibold text-red-400">-£{prop.inventoryCosts.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-gray-400">Occupancy</span>
                          <div className="font-semibold">{prop.occupancyRate}%</div>
                        </div>
                        <div>
                          <span className="text-gray-400">ADR</span>
                          <div className="font-semibold">£{prop.adr.toFixed(2)}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : <Loader />}
        </TabsContent>

        {/* ── Expenses ─────────────────────────────────────────────────── */}
        <TabsContent value="expenses">
          <div className="flex justify-between items-center mb-4">
            <Select value={expensePeriod} onValueChange={setExpensePeriod}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3months">3 Months</SelectItem>
                <SelectItem value="6months">6 Months</SelectItem>
                <SelectItem value="12months">12 Months</SelectItem>
                <SelectItem value="ytd">Year to Date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {expenses ? (
            <div className="space-y-6">
              <Card className="bg-gradient-to-r from-[#1F3A68] to-[#2D5294] text-white">
                <CardContent className="py-5 px-6">
                  <div className="text-sm opacity-80 mb-1">Total Expenses</div>
                  <div className="text-3xl font-bold">
                    £{expenses.totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Category breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Expense Categories</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-6">
                      <ResponsiveContainer width="45%" height={200}>
                        <PieChart>
                          <Pie
                            data={expenses.categories.filter(c => c.amount > 0)}
                            dataKey="amount"
                            nameKey="category"
                            cx="50%" cy="50%"
                            outerRadius={80}
                            innerRadius={45}
                          >
                            {expenses.categories.filter(c => c.amount > 0).map((_, idx) => (
                              <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip formatter={(v) => `£${v.toFixed(2)}`} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="flex-1 space-y-3">
                        {expenses.categories.map((cat, idx) => (
                          <div key={cat.category}>
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                                <span>{cat.category}</span>
                              </div>
                              <div className="text-right">
                                <span className="font-medium">£{cat.amount.toFixed(2)}</span>
                                <span className="text-gray-400 text-xs ml-2">({cat.percentage}%)</span>
                              </div>
                            </div>
                            {cat.breakdown && cat.breakdown.length > 0 && (
                              <div className="ml-5 mt-1 space-y-0.5">
                                {cat.breakdown.map(sub => (
                                  <div key={sub.type} className="flex justify-between text-xs text-gray-400">
                                    <span className="capitalize">{sub.type}</span>
                                    <span>£{sub.amount.toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Monthly trend */}
                {expenses.monthlyTrend.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Monthly Expense Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={expenses.monthlyTrend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} />
                          <RechartsTooltip formatter={(v, name) => [`£${v.toFixed(2)}`, name]} />
                          <Legend />
                          <Bar dataKey="fees" name="Fees" stackId="a" fill="#1F3A68" />
                          <Bar dataKey="vendor" name="Vendor" stackId="a" fill="#CB3B3B" />
                          <Bar dataKey="inventory" name="Inventory" stackId="a" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          ) : <Loader />}
        </TabsContent>

        {/* ── Benchmarks ───────────────────────────────────────────────── */}
        <TabsContent value="benchmarks">
          {benchmarks ? (
            <div className="space-y-6">
              <p className="text-sm text-gray-500">
                Compare your current 30-day performance against your own historical averages
              </p>

              {/* Your earnings target */}
              <Card className="border-[#1F3A68]/20 bg-[#1F3A68]/5">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-[#1F3A68]" />
                      <span className="font-semibold text-[#1F3A68]">Your monthly earnings target</span>
                    </div>
                    {!editingTarget && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setTargetInput(benchmarks.target?.monthlyTarget ? String(benchmarks.target.monthlyTarget) : ''); setEditingTarget(true); }}
                      >
                        {benchmarks.target?.monthlyTarget ? 'Edit target' : 'Set target'}
                      </Button>
                    )}
                  </div>

                  {editingTarget ? (
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1 max-w-[220px]">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
                        <input
                          type="number"
                          min="0"
                          value={targetInput}
                          onChange={(e) => setTargetInput(e.target.value)}
                          placeholder="e.g. 3000"
                          className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 text-sm"
                          autoFocus
                        />
                      </div>
                      <Button size="sm" className="bg-[#1F3A68]" disabled={savingTarget} onClick={saveTarget}>
                        {savingTarget ? 'Saving…' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingTarget(false)}>Cancel</Button>
                    </div>
                  ) : benchmarks.target?.monthlyTarget ? (
                    <>
                      <div className="flex items-end gap-3 mb-2">
                        <div className="text-2xl font-bold text-[#1F3A68]">
                          £{(benchmarks.target.currentRevenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </div>
                        <div className="text-sm text-gray-500 mb-0.5">
                          of £{benchmarks.target.monthlyTarget.toLocaleString(undefined, { maximumFractionDigits: 0 })} this month
                        </div>
                        <div className={`ml-auto text-sm font-semibold ${benchmarks.target.onTrack ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {benchmarks.target.attainmentPct != null ? `${benchmarks.target.attainmentPct}%` : ''}
                        </div>
                      </div>
                      <div className="h-2.5 bg-[#1F3A68]/10 rounded-full overflow-hidden">
                        <div
                          className={`h-2.5 rounded-full ${benchmarks.target.onTrack ? 'bg-emerald-500' : 'bg-[#1F3A68]'}`}
                          style={{ width: `${Math.min(100, benchmarks.target.attainmentPct || 0)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {benchmarks.target.onTrack
                          ? `You're £${Math.abs(benchmarks.target.delta || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} over target this month.`
                          : `£${Math.abs(benchmarks.target.delta || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} to go to hit your target this month.`}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">Set a monthly earnings target to track this month's revenue against your own goal.</p>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* ADR Benchmark */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-[#1F3A68]" /> Average Daily Rate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-3 mb-4">
                      <div className="text-3xl font-bold text-[#1F3A68]">£{benchmarks.benchmarks.adr.current.toFixed(2)}</div>
                      <TrendBadge {...benchmarks.benchmarks.adr.trend} />
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">3M Avg</div>
                        <div className="font-medium">£{benchmarks.benchmarks.adr.threeMonth.toFixed(2)}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">6M Avg</div>
                        <div className="font-medium">£{benchmarks.benchmarks.adr.sixMonth.toFixed(2)}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">12M Avg</div>
                        <div className="font-medium">£{benchmarks.benchmarks.adr.twelveMonth.toFixed(2)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Profit Margin Benchmark */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Target className="h-4 w-4 text-emerald-600" /> Profit Margin
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-3 mb-4">
                      <div className="text-3xl font-bold text-emerald-600">{benchmarks.benchmarks.profitMargin.current}%</div>
                      <TrendBadge {...benchmarks.benchmarks.profitMargin.trend} />
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">3M Avg</div>
                        <div className="font-medium">{benchmarks.benchmarks.profitMargin.threeMonth}%</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">6M Avg</div>
                        <div className="font-medium">{benchmarks.benchmarks.profitMargin.sixMonth}%</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">12M Avg</div>
                        <div className="font-medium">{benchmarks.benchmarks.profitMargin.twelveMonth}%</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Revenue Benchmark */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Wallet className="h-4 w-4 text-[#1F3A68]" /> Monthly Revenue
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-3 mb-4">
                      <div className="text-3xl font-bold text-[#1F3A68]">
                        £{benchmarks.benchmarks.revenuePerMonth.current.toFixed(0)}
                      </div>
                      <TrendBadge {...benchmarks.benchmarks.revenuePerMonth.trend} />
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">3M Avg</div>
                        <div className="font-medium">£{benchmarks.benchmarks.revenuePerMonth.threeMonthAvg.toFixed(0)}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">6M Avg</div>
                        <div className="font-medium">£{benchmarks.benchmarks.revenuePerMonth.sixMonthAvg.toFixed(0)}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">12M Avg</div>
                        <div className="font-medium">£{benchmarks.benchmarks.revenuePerMonth.twelveMonthAvg.toFixed(0)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Cost Per Booking Benchmark */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-amber-600" /> Cost Per Booking
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-3 mb-4">
                      <div className="text-3xl font-bold text-amber-600">
                        £{benchmarks.benchmarks.costPerBooking.current.toFixed(2)}
                      </div>
                      {/* For cost, down is good */}
                      <TrendBadge
                        direction={benchmarks.benchmarks.costPerBooking.trend.direction === 'up' ? 'down' : benchmarks.benchmarks.costPerBooking.trend.direction === 'down' ? 'up' : 'flat'}
                        change={benchmarks.benchmarks.costPerBooking.trend.change}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">3M Avg</div>
                        <div className="font-medium">£{benchmarks.benchmarks.costPerBooking.threeMonth.toFixed(2)}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">6M Avg</div>
                        <div className="font-medium">£{benchmarks.benchmarks.costPerBooking.sixMonth.toFixed(2)}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-gray-400 text-xs">12M Avg</div>
                        <div className="font-medium">£{benchmarks.benchmarks.costPerBooking.twelveMonth.toFixed(2)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : <Loader />}
        </TabsContent>

        {/* ── AI P&L Insights ─────────────────────────────────────────── */}
        <TabsContent value="ai-insights">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              Rule-based analysis of your revenue, costs, and operations — with actionable recommendations
            </p>
            <Select value={aiPeriod} onValueChange={setAiPeriod}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1months">Last month</SelectItem>
                <SelectItem value="3months">Last 3 months</SelectItem>
                <SelectItem value="6months">Last 6 months</SelectItem>
                <SelectItem value="12months">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {aiInsights ? (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-gray-500">Revenue</p>
                    <p className="text-xl font-bold text-[#1F3A68]">{'\u00a3'}{aiInsights.summary.totalRevenue.toFixed(0)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-gray-500">Costs</p>
                    <p className="text-xl font-bold text-red-600">{'\u00a3'}{aiInsights.summary.totalCosts.toFixed(0)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-gray-500">Bookings</p>
                    <p className="text-xl font-bold text-[#1F3A68]">{aiInsights.summary.bookingCount}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-gray-500">Properties</p>
                    <p className="text-xl font-bold text-[#1F3A68]">{aiInsights.summary.propertyCount}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Insight Cards */}
              {aiInsights.insights.length === 0 ? (
                <Card className="bg-gray-50">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="h-12 w-12 mx-auto text-emerald-400 mb-3" />
                    <h3 className="font-bold text-gray-700">Looking Good!</h3>
                    <p className="text-sm text-gray-500 mt-1">No issues detected for this period. Keep up the great work.</p>
                  </CardContent>
                </Card>
              ) : (
                aiInsights.insights.map(insight => {
                  const style = SEVERITY_STYLES[insight.severity] || SEVERITY_STYLES.info;
                  const Icon = style.icon;
                  return (
                    <Card key={insight.id} className={`border ${style.border} ${style.bg}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <Icon className={`h-6 w-6 ${style.text} flex-shrink-0 mt-0.5`} />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className={`font-bold ${style.text}`}>{insight.title}</h4>
                              {insight.metric && (
                                <Badge variant={style.badge} className="ml-2">{insight.metric}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 mb-3">{insight.body}</p>
                            <div className="p-3 bg-white/60 rounded-lg">
                              <p className="text-sm">
                                <span className="font-medium text-[#1F3A68]">Recommendation: </span>
                                {insight.recommendation}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          ) : <Loader />}
        </TabsContent>
      </Tabs>

      {/* Per-property P&L drill-down */}
      {pnlDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setPnlDetail(null)}>
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start justify-between pb-3 border-b">
                <div>
                  <h3 className="text-lg font-bold text-[#1F3A68]">{pnlDetail.propertyName}</h3>
                  <p className="text-sm text-gray-500">
                    {pnlDetail.location ? `${pnlDetail.location} · ` : ''}<span className="capitalize">{pnlDetail.status}</span> · this {propertyPnl?.period?.replace('months', ' months') || 'period'}
                  </p>
                </div>
                <button onClick={() => setPnlDetail(null)} className="text-gray-400 hover:text-gray-600"><XCircle size={22} /></button>
              </div>

              {/* Net profit headline */}
              <div className="text-center py-2">
                <div className={`text-3xl font-bold ${pnlDetail.netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>£{pnlDetail.netProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className="text-sm text-gray-500">net profit · {pnlDetail.profitMargin}% margin</div>
              </div>

              {/* P&L statement */}
              <div className="border rounded-lg divide-y text-sm">
                <div className="flex justify-between px-4 py-2 font-semibold text-[#1F3A68]"><span>Gross revenue</span><span>£{pnlDetail.grossRevenue.toFixed(2)}</span></div>
                <div className="flex justify-between px-4 py-2 text-gray-600"><span>Platform fee</span><span className="text-red-500">−£{pnlDetail.platformFees.toFixed(2)}</span></div>
                <div className="flex justify-between px-4 py-2 text-gray-600"><span>Stripe fees</span><span className="text-red-500">−£{pnlDetail.stripeFees.toFixed(2)}</span></div>
                <div className="flex justify-between px-4 py-2 text-gray-600"><span>Vendor costs</span><span className="text-red-500">−£{pnlDetail.vendorCosts.toFixed(2)}</span></div>
                <div className="flex justify-between px-4 py-2 text-gray-600"><span>Inventory</span><span className="text-red-500">−£{pnlDetail.inventoryCosts.toFixed(2)}</span></div>
                {typeof pnlDetail.vatCollected === 'number' && pnlDetail.vatCollected > 0 && (
                  <div className="flex justify-between px-4 py-2 text-gray-500"><span>VAT collected</span><span>£{pnlDetail.vatCollected.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between px-4 py-2 font-semibold text-gray-700 bg-gray-50"><span>Total costs</span><span className="text-red-600">−£{pnlDetail.totalCosts.toFixed(2)}</span></div>
              </div>

              {/* Operational metrics */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Performance</p>
                <div className="grid grid-cols-3 gap-3 text-center">
                  {[
                    { label: 'Bookings', value: pnlDetail.bookings },
                    { label: 'Nights', value: pnlDetail.nightsBooked },
                    { label: 'Occupancy', value: `${pnlDetail.occupancyRate}%` },
                    { label: 'ADR', value: `£${pnlDetail.adr.toFixed(0)}` },
                    { label: 'RevPAR', value: `£${(pnlDetail.revpar || 0).toFixed(0)}` },
                    { label: 'Cost / booking', value: `£${(pnlDetail.costPerBooking || 0).toFixed(0)}` },
                  ].map((m) => (
                    <div key={m.label} className="p-2 bg-gray-50 rounded">
                      <div className="text-[11px] text-gray-400">{m.label}</div>
                      <div className="font-semibold text-[#1F3A68]">{m.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
